import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Layout,
  Typography,
  Button,
  Card,
  Row,
  Col,
  Space,
  Avatar,
  message,
} from 'antd';
import {
  HomeOutlined,
  HeartOutlined,
  FileOutlined,
  PhoneOutlined,
  UserOutlined,
  LogoutOutlined,
} from '@ant-design/icons';
import './Home.css';

const { Header, Content, Footer } = Layout;

const Home = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoveredHero, setHoveredHero] = useState(false);
  const navigate = useNavigate();

  const handleCardHover = (cardId) => {
    setHoveredCard(cardId);
  };

  const handleHeroHover = () => {
    setHoveredHero(true);
  };

  const handleHeroLeave = () => {
    setHoveredHero(false);
  };

  const handleLogout = async () => {
    try {
      const response = await fetch('/backend/logout.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok) {
        message.success(data.message || 'Déconnexion réussie');
        navigate('/login');
      } else {
        message.error(data.error || 'Erreur lors de la déconnexion.');
      }
    } catch (error) {
      message.error('Une erreur s\'est produite lors de la déconnexion.');
    }
  };

  return (
    <Layout style={{ minHeight: '100vh', background: '#f9fafb' }}>
      {/* Navbar */}
      <Header
        style={{
          background: '#fff',
          padding: '0 30px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
          borderBottom: '1px solid #e5e7eb',
          transition: 'all 0.3s ease',
        }}
        onMouseEnter={() => setHoveredCard('nav')}
        onMouseLeave={() => setHoveredCard(null)}
      >
        <Space align="center">
          <Avatar
            icon={<UserOutlined />}
            style={{ backgroundColor: '#2b6cb0', marginRight: 12, transition: 'transform 0.3s ease' }}
            className={hoveredCard === 'nav' ? 'nav-avatar-hover' : ''}
          />
          <Typography.Title
            level={4}
            style={{ margin: 0, color: '#2b6cb0', fontWeight: 600, transition: 'color 0.3s ease' }}
            className={hoveredCard === 'nav' ? 'nav-title-hover' : ''}
          >
            NH Guide
          </Typography.Title>
        </Space>
        <div style={{ flex: 1 }} />
        <Space size={20}>
          <Link
            to="/profile"
            style={{ color: '#1a1a1a', fontWeight: 500, padding: '0 15px', transition: 'color 0.3s ease' }}
            onMouseEnter={(e) => (e.target.style.color = '#1e4a8a')}
            onMouseLeave={(e) => (e.target.style.color = '#1a1a1a')}
          >
            Profile
          </Link>
          <Link
            to="https://app.nhguide.org/ServiceProviders "
            style={{ color: '#1a1a1a', fontWeight: 500, padding: '0 15px', transition: 'color 0.3s ease' }}
            onMouseEnter={(e) => (e.target.style.color = '#1e4a8a')}
            onMouseLeave={(e) => (e.target.style.color = '#1a1a1a')}
          >
            Services and Resources
          </Link>
          <Link
            to="/DataPrivacy"
            style={{ color: '#1a1a1a', fontWeight: 500, padding: '0 15px', transition: 'color 0.3s ease' }}
            onMouseEnter={(e) => (e.target.style.color = '#1e4a8a')}
            onMouseLeave={(e) => (e.target.style.color = '#1a1a1a')}
          >
            Privacy and Data Sharing
          </Link>
          <Button
            type="link"
            icon={<LogoutOutlined />}
            style={{ color: '#1a1a1a', fontWeight: 500, padding: '0 15px', transition: 'color 0.3s ease' }}
            onMouseEnter={(e) => (e.target.style.color = '#e74c3c')}
            onMouseLeave={(e) => (e.target.style.color = '#1a1a1a')}
            onClick={handleLogout}
          >
            Logout
          </Button>
        </Space>
      </Header>

      {/* Hero Section */}
      <Content style={{ padding: '0 50px', marginTop: 40 }}>
        <div
          style={{
            background: 'linear-gradient(90deg, #4ecdc4 0%, #45b7d1 50%, #2b6cb0 100%)',
            borderRadius: '16px',
            padding: '60px 40px',
            textAlign: 'center',
            color: '#fff',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.4s ease',
            transform: hoveredHero ? 'translateY(-10px)' : 'translateY(0)',
          }}
          onMouseEnter={handleHeroHover}
          onMouseLeave={handleHeroLeave}
        >
          <Typography.Title
            level={2}
            style={{ color: '#fff', margin: 0, fontWeight: 700, lineHeight: '1.2', transition: 'opacity 0.3s ease' }}
            className={hoveredHero ? 'hero-title-hover' : ''}
          >
            Navigating Homelessness Guide
          </Typography.Title>
          <Typography.Text
            style={{
              color: 'rgba(255, 255, 255, 0.85)',
              fontSize: 18,
              display: 'block',
              marginTop: 12,
              fontWeight: 500,
              transition: 'opacity 0.3s ease',
            }}
            className={hoveredHero ? 'hero-subtitle-hover' : ''}
          >
            Information and tools to help you regain your housing
          </Typography.Text>
          <Space size={24} style={{ marginTop: 32 }}>
            <Button
              type="primary"
              shape="round"
              size="large"
              href="/get-started"
              style={{
                backgroundColor: '#2b6cb0',
                borderColor: '#2b6cb0',
                color: '#fff',
                padding: '12px 32px',
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(43, 108, 176, 0.3)',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#1e4a8a';
                e.target.style.borderColor = '#1e4a8a';
                e.target.style.boxShadow = '0 6px 15px rgba(30, 74, 138, 0.4)';
                e.target.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#2b6cb0';
                e.target.style.borderColor = '#2b6cb0';
                e.target.style.boxShadow = '0 4px 12px rgba(43, 108, 176, 0.3)';
                e.target.style.transform = 'scale(1)';
              }}
            >
              Get Started
            </Button>
            <Button
              type="default"
              shape="circle"
              href="/support"
              icon={<PhoneOutlined style={{ color: '#2b6cb0', fontSize: 16 }} />}
              style={{
                borderColor: '#fff',
                color: '#fff',
                width: 48,
                height: 48,
                padding: 0,
                background: 'rgba(255, 255, 255, 0.1)',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                e.target.style.transform = 'rotate(360deg)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                e.target.style.transform = 'rotate(0deg)';
              }}
            />
            support
          </Space>
        </div>

        {/* Cards Section */}
        <Row gutter={[24, 24]} style={{ marginTop: 48 }}>
          <Col xs={24} sm={12} md={6}>
            <Card
              hoverable
              style={{
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                textAlign: 'center',
                transition: 'all 0.4s ease',
                height: '350px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
              onMouseEnter={() => handleCardHover(1)}
              onMouseLeave={() => handleCardHover(null)}
              className={hoveredCard === 1 ? 'card-hover' : ''}
            >
              <Card.Meta
                avatar={<HomeOutlined style={{ fontSize: 40, color: '#2b6cb0' }} />}
                title={<Typography.Text strong style={{ fontSize: 18 }}>Manage Profile</Typography.Text>}
                description={
                  <Typography.Text type="secondary" style={{ fontSize: 14 }}>
                    Manage your personal information and housing status
                  </Typography.Text>
                }
              />
              <Space size={12} style={{ marginTop: 20 }}>
                <Button
                  type="primary"
                  shape="round"
                  href="/profile"
                  style={{ backgroundColor: '#2b6cb0', borderColor: '#2b6cb0', padding: '8px 20px', fontWeight: 500 }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#1e4a8a';
                    e.target.style.borderColor = '#1e4a8a';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#2b6cb0';
                    e.target.style.borderColor = '#2b6cb0';
                  }}
                >
                  Profile
                </Button>
                <Button
                  type="default"
                  shape="round"
                  href="/ContactForm"
                  style={{ borderColor: '#d9d9d9', padding: '8px 20px', fontWeight: 500 }}
                  onMouseEnter={(e) => (e.target.style.borderColor = '#1e4a8a')}
                  onMouseLeave={(e) => (e.target.style.borderColor = '#d9d9d9')}
                >
                  Contacts
                </Button>
              </Space>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card
              hoverable
              style={{
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                textAlign: 'center',
                transition: 'all 0.4s ease',
                height: '350px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
              onMouseEnter={() => handleCardHover(2)}
              onMouseLeave={() => handleCardHover(null)}
              className={hoveredCard === 2 ? 'card-hover' : ''}
            >
              <Card.Meta
                avatar={<HeartOutlined style={{ fontSize: 40, color: '#2b6cb0' }} />}
                title={<Typography.Text strong style={{ fontSize: 18 }}>Support Services and Resources</Typography.Text>}
                description={
                  <Typography.Text type="secondary" style={{ fontSize: 14 }}>
                    Support Services and Providers Information
                  </Typography.Text>
                }
              />
              <Space size={12} style={{ marginTop: 12 }}>
                <Button
                  type="link"
                  href="/ServiceProviders"
                  icon={<PhoneOutlined style={{ color: '#2b6cb0', fontSize: 16 }} />}
                  style={{ padding: 0, fontSize: 14, color: '#2b6cb0' }}
                  onMouseEnter={(e) => (e.target.style.color = '#1e4a8a')}
                  onMouseLeave={(e) => (e.target.style.color = '#2b6cb0')}
                >
                  Support
                </Button>
                
              </Space>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card
              hoverable
              style={{
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                textAlign: 'center',
                transition: 'all 0.4s ease',
                height: '350px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
              onMouseEnter={() => handleCardHover(3)}
              onMouseLeave={() => handleCardHover(null)}
              className={hoveredCard === 3 ? 'card-hover' : ''}
            >
              <Card.Meta
                avatar={<FileOutlined style={{ fontSize: 40, color: '#2b6cb0' }} />}
                title={<Typography.Text strong style={{ fontSize: 18 }}>Privacy and Data Sharing</Typography.Text>}
                description={
                  <Typography.Text type="secondary" style={{ fontSize: 14 }}>
                    Access guides and educational materials
                  </Typography.Text>
                }
              />
              <Space size={12} style={{ marginTop: 12 }}>
                <Button
                  type="link"
                  href="/privacy"
                  style={{ padding: 0, fontSize: 14, color: '#2b6cb0' }}
                  onMouseEnter={(e) => (e.target.style.color = '#1e4a8a')}
                  onMouseLeave={(e) => (e.target.style.color = '#2b6cb0')}
                >
                  View Privacy Policy
                </Button>
              </Space>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card
              hoverable
              style={{
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                textAlign: 'center',
                transition: 'all 0.4s ease',
                height: '350px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
              onMouseEnter={() => handleCardHover(4)}
              onMouseLeave={() => handleCardHover(null)}
              className={hoveredCard === 4 ? 'card-hover' : ''}
            >
              <Card.Meta
                avatar={<HomeOutlined style={{ fontSize: 40, color: '#2b6cb0' }} />}
                title={<Typography.Text strong style={{ fontSize: 18 }}>Housing Vulnerability and Regaining Housing</Typography.Text>}
                description={
                  <Typography.Text type="secondary" style={{ fontSize: 14 }}>
                    Materials to understand your housing challenges and options to access support and regain housing
                  </Typography.Text>
                }
              />
              <Space size={12} style={{ marginTop: 12 }}>
                <Button
                  type="link"
                  href="/resources"
                  style={{ padding: 0, fontSize: 14, color: '#2b6cb0' }}
                  onMouseEnter={(e) => (e.target.style.color = '#1e4a8a')}
                  onMouseLeave={(e) => (e.target.style.color = '#2b6cb0')}
                >
                  Access Resources
                </Button>
                <Button
                  type="link"
                  href="https://nhguide.org/courses/ "
                  style={{ padding: 0, fontSize: 14, color: '#2b6cb0' }}
                  onMouseEnter={(e) => (e.target.style.color = '#1e4a8a')}
                  onMouseLeave={(e) => (e.target.style.color = '#2b6cb0')}
                >
                  Courses
                </Button>
              </Space>
            </Card>
          </Col>
        </Row>
      </Content>

      {/* Footer */}
      <Footer
        style={{
          textAlign: 'center',
          padding: '20px 0',
          background: '#fff',
          boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.05)',
          borderTop: '1px solid #e5e7eb',
          transition: 'all 0.3s ease',
        }}
        onMouseEnter={() => setHoveredCard('footer')}
        onMouseLeave={() => setHoveredCard(null)}
      >
        <Space size={24}>
          <Button
            type="link"
            href="/support"
            style={{ color: '#2b6cb0', fontWeight: 500, fontSize: 14, transition: 'color 0.3s ease' }}
            onMouseEnter={(e) => (e.target.style.color = '#1e4a8a')}
            onMouseLeave={(e) => (e.target.style.color = '#2b6cb0')}
          >
            Support
          </Button>
          <Button
            type="link"
            href="/support"
            style={{ color: '#2b6cb0', fontWeight: 500, fontSize: 14, transition: 'color 0.3s ease' }}
            onMouseEnter={(e) => (e.target.style.color = '#1e4a8a')}
            onMouseLeave={(e) => (e.target.style.color = '#2b6cb0')}
          >
            ACHRA CoA Assistant
          </Button>
          <Button
            type="link"
            href="/privacy"
            style={{ color: '#2b6cb0', fontWeight: 500, fontSize: 14, transition: 'color 0.3s ease' }}
            onMouseEnter={(e) => (e.target.style.color = '#1e4a8a')}
            onMouseLeave={(e) => (e.target.style.color = '#2b6cb0')}
          >
            View Privacy Policy
          </Button>
          <Button
            type="link"
            href="/resources"
            style={{ color: '#2b6cb0', fontWeight: 500, fontSize: 14, transition: 'color 0.3s ease' }}
            onMouseEnter={(e) => (e.target.style.color = '#1e4a8a')}
            onMouseLeave={(e) => (e.target.style.color = '#2b6cb0')}
          >
            Access Resources
          </Button>
          <Button
            type="link"
            href="https://nhguide.org/courses/"
            style={{ color: '#2b6cb0', fontWeight: 500, fontSize: 14, transition: 'color 0.3s ease' }}
            onMouseEnter={(e) => (e.target.style.color = '#1e4a8a')}
            onMouseLeave={(e) => (e.target.style.color = '#2b6cb0')}
          >
            Courses
          </Button>
        </Space>
      </Footer>
    </Layout>
  );
};

export default Home;